import { observer } from 'mobx-react-lite';
import { useComputed } from 'owa-react-hooks/lib/useComputed';
import { appTasks } from 'owa-locstrings/lib/strings/apptasks.locstring.json';
import { appToDo } from 'owa-locstrings/lib/strings/apptodo.locstring.json';
import { appPeople } from 'owa-locstrings/lib/strings/apppeople.locstring.json';
import { appCalendar } from 'owa-locstrings/lib/strings/appcalendar.locstring.json';
import { appOutlookSpaces } from 'owa-locstrings/lib/strings/appOutlookSpaces.locstring.json';
import { mailAppName, appFiles, overflowButtonTitle } from './ModuleSwitcher.locstring.json';
import loc from 'owa-localize';
import { OverflowSet, IOverflowSetItemProps } from 'office-ui-fabric-react/lib/OverflowSet';
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import * as React from 'react';
import { ControlIcons } from 'owa-control-icons';
import { CommandBarButton } from 'office-ui-fabric-react/lib/Button';
import { Module } from '../utils/Module';
import { isFeatureEnabled } from 'owa-feature-flags';
import ModuleSwitcherItem from './ModuleSwitcherItem';
import { initializeTabReuse } from 'owa-tab-reuse';
import { canUseToDoFeatures } from 'owa-todo-utils/lib/utils/canUseToDoFeatures';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import getModuleSwitcherItem from '../utils/getModuleSwitcherItem';

let styles = require('./ModuleSwitcher.scss');
let classNames = require('classnames/bind').bind(styles);

export interface ModuleSwitcherProps extends React.HTMLAttributes<HTMLAnchorElement> {
    selectedModule: Module;
    isHorizontal?: boolean;
    containerWidth?: number; // Specify container width if module switcher is horizontal for number of button calculations.
    /**
     * The action to call when the item is clicked and it also represents the active module.
     * - (eg, clicking mail while in mail)
     */
    activeModuleAction?: (
        ev?: React.MouseEvent<unknown> | React.KeyboardEvent<HTMLElement>
    ) => void;
}

export default observer(function ModuleSwitcher(props: ModuleSwitcherProps) {
    React.useEffect(() => {
        initializeTabReuse(Module[props.selectedModule]);
    }, []);
    const moduleSwitcherItems = useComputed((): IOverflowSetItemProps[] => {
        return getModuleSwitcherItems(props.selectedModule, props.activeModuleAction);
    });
    const renderItem = (item: IOverflowSetItemProps) => {
        const { selectedModule, activeModuleAction, isHorizontal } = props;
        return (
            <ModuleSwitcherItem
                iconProps={item.iconProps}
                workload={item.workload}
                text={item.text}
                selectedModule={selectedModule}
                activeModuleAction={activeModuleAction}
                isHorizontal={isHorizontal}
                href={item.href}
            />
        );
    };
    const moduleButtonSize = isFeatureEnabled('fwk-fluentCommandBar-moduleswitcher') ? 56 : 48;
    const containerRightMargin = isFeatureEnabled('fwk-fluentCommandBar-moduleswitcher') ? 4 : 0;
    const { isHorizontal, containerWidth } = props;
    const containerAriaProps: AriaProperties = {
        role: AriaRoles.menubar,
        orientation: isHorizontal ? 'horizontal' : 'vertical',
    };
    let buttonsToShow = moduleSwitcherItems.get();
    let overflowItems = [];
    if (isHorizontal) {
        if (!containerWidth) {
            throw new Error(
                'When module switcher is horizontal, a container width must be passed in'
            );
        }
        // Get the number of buttons that fit within container (minus the overflow button width)
        const numButtons = Math.floor((containerWidth - containerRightMargin) / moduleButtonSize);
        if (numButtons < moduleSwitcherItems.get().length) {
            // Subtract 1 to account for the overflow button
            buttonsToShow = moduleSwitcherItems.get().slice(0, numButtons - 1);
            overflowItems = moduleSwitcherItems.get().slice(numButtons - 1);
        }
    }
    return (
        <OverflowSet
            className={isHorizontal && styles.horizontalContainer}
            vertical={!isHorizontal}
            items={buttonsToShow}
            overflowItems={overflowItems}
            onRenderItem={renderItem}
            onRenderOverflowButton={renderOverflowItem}
            {...generateDomPropertiesForAria(containerAriaProps)}
        />
    );
});

export function getModuleSwitcherItems(
    selectedModule: Module,
    activeModuleAction?: (ev?: React.MouseEvent<unknown> | React.KeyboardEvent<HTMLElement>) => void
): IOverflowSetItemProps[] {
    const moduleSwitcherItems: IOverflowSetItemProps[] = [
        getModuleSwitcherItem(
            Module.Mail,
            selectedModule,
            loc(mailAppName),
            ControlIcons.Mail,
            activeModuleAction
        ),
        getModuleSwitcherItem(
            Module.Calendar,
            selectedModule,
            loc(appCalendar),
            ControlIcons.Calendar,
            activeModuleAction
        ),
        getModuleSwitcherItem(
            Module.People,
            selectedModule,
            loc(appPeople),
            ControlIcons.People,
            activeModuleAction
        ),
    ];

    if (isFeatureEnabled('doc-fileshub')) {
        moduleSwitcherItems.push(
            getModuleSwitcherItem(
                Module.FilesHub,
                selectedModule,
                loc(appFiles),
                ControlIcons.Attach,
                activeModuleAction
            )
        );
    }

    if (!getUserConfiguration().SessionSettings.IsShadowMailbox) {
        const showToDoModule = canUseToDoFeatures();
        const displayName = showToDoModule ? loc(appToDo) : loc(appTasks);
        const icon = showToDoModule ? ControlIcons.ToDoLogoOutline : ControlIcons.CheckboxComposite;
        moduleSwitcherItems.push(
            getModuleSwitcherItem(
                Module.Tasks,
                selectedModule,
                displayName,
                icon,
                activeModuleAction
            )
        );
    }

    if (isFeatureEnabled('outlookSpaces-enabled')) {
        moduleSwitcherItems.push(
            getModuleSwitcherItem(
                Module.OutlookSpaces,
                selectedModule,
                loc(appOutlookSpaces),
                ControlIcons.NotePinned,
                activeModuleAction
            )
        );
    }

    return moduleSwitcherItems;
}

function renderOverflowItem(overflowItems: IOverflowSetItemProps[]) {
    return (
        <div className={styles.moduleSwitcherButtonContainer}>
            <CommandBarButton
                key={'overflow'}
                styles={{
                    root: classNames(
                        styles.root,
                        isFeatureEnabled('fwk-fluentCommandBar-moduleswitcher') && styles.fluent
                    ),
                    menuIcon: styles.menuIcon,
                }}
                menuIconProps={{ iconName: ControlIcons.More }}
                menuProps={{ items: overflowItems }}
                title={loc(overflowButtonTitle)}
            />
        </div>
    );
}
